import api from './api';
import { CommercialVisitFormValues, CommercialVisitType } from '../types/commercialVisit.types';

const commercialVisitService = {
  // Obter todas as visitas comerciais com filtros
  async getCommercialVisits(params?: {
    client?: string;
    type?: string;
    status?: string;
    priority?: string;
    assignedTo?: string;
    startDate?: string;
    endDate?: string;
    search?: string;
    page?: number;
    limit?: number;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  }) {
    console.log('CommercialVisitService: Buscando visitas comerciais...');
    try {
      // Fazer a chamada à API com os parâmetros fornecidos
      const response = await api.get('/commercial-visits', { params });
      console.log('CommercialVisitService: Resposta da API:', response.data);
      
      // Lidar com diferentes formatos de resposta
      if (response.data && Array.isArray(response.data)) {
        // Se a resposta for um array direto
        console.log(`CommercialVisitService: ${response.data.length} visitas encontradas (array direto)`);
        return { visits: response.data, totalCount: response.data.length };
      } else if (response.data && Array.isArray(response.data.visits)) {
        // Se a resposta já estiver no formato esperado
        console.log(`CommercialVisitService: ${response.data.visits.length} visitas encontradas (formato padrão)`);
        return response.data;
      } else if (response.data && response.data.commercialVisits) {
        // Formato retornado pela API atual
        console.log(`CommercialVisitService: ${response.data.commercialVisits.length} visitas encontradas (formato API)`);
        return { 
          visits: response.data.commercialVisits, 
          totalCount: response.data.pagination?.total || response.data.commercialVisits.length 
        };
      } else {
        console.warn('CommercialVisitService: Formato de resposta inesperado:', response.data);
        return { visits: [], totalCount: 0 };
      }
    } catch (error) {
      console.error('CommercialVisitService: Erro ao buscar visitas:', error);
      return { visits: [], totalCount: 0 };
    }
  },

  // Obter uma visita comercial específica por ID
  async getCommercialVisitById(id: string) {
    const response = await api.get(`/commercial-visits/${id}`);
    return response.data.commercialVisit;
  },

  // Criar uma nova visita comercial
  async createCommercialVisit(visitData: CommercialVisitFormValues) {
    // Remover campos vazios para evitar problemas com validação
    const cleanedData = { ...visitData };
    if (!cleanedData.representative || cleanedData.representative === '') {
      delete cleanedData.representative;
    }
    if (!cleanedData.checklistTemplate || cleanedData.checklistTemplate === '') {
      delete cleanedData.checklistTemplate;
    }
    
    console.log('CommercialVisitService: Enviando dados:', JSON.stringify(cleanedData, null, 2));
    const response = await api.post('/commercial-visits', cleanedData);
    return response.data;
  },

  // Atualizar uma visita comercial existente
  async updateCommercialVisit(id: string, visitData: Partial<CommercialVisitFormValues>) {
    // Remover campos vazios para evitar problemas com validação
    const cleanedData = { ...visitData };
    if (!cleanedData.representative || cleanedData.representative === '') {
      delete cleanedData.representative;
    }
    if (!cleanedData.checklistTemplate || cleanedData.checklistTemplate === '') {
      delete cleanedData.checklistTemplate;
    }
    
    console.log('CommercialVisitService: Atualizando visita com dados:', JSON.stringify(cleanedData, null, 2));
    const response = await api.put(`/commercial-visits/${id}`, cleanedData);
    return response.data;
  },

  // Excluir uma visita comercial (soft delete)
  async deleteCommercialVisit(id: string) {
    const response = await api.delete(`/commercial-visits/${id}`);
    return response.data;
  },

  // Adicionar uma ação à visita comercial
  async addAction(id: string, actionData: { description: string; status?: string }) {
    const response = await api.post(`/commercial-visits/${id}/actions`, actionData);
    return response.data;
  },

  // Atualizar o status da visita comercial
  async updateStatus(id: string, statusData: { status: string; notes?: string }) {
    const response = await api.put(`/commercial-visits/${id}/status`, statusData);
    return response.data;
  },

  // Obter visitas comerciais por cliente
  async getVisitsByClient(clientId: string) {
    const response = await api.get(`/commercial-visits/client/${clientId}`);
    return response.data.visits;
  },

  // Formatar data
  formatDate(date: string | Date): string {
    const dateObj = date instanceof Date ? date : new Date(date);
    return dateObj.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  },

  // Formatar data e hora
  formatDateTime(date: string | Date): string {
    const dateObj = date instanceof Date ? date : new Date(date);
    return dateObj.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }
};

export default commercialVisitService;
